import { configureStore, combineReducers, AnyAction, Reducer } from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "reduxjs-toolkit-persist";
import localStorage from "reduxjs-toolkit-persist/lib/storage";
import { multisiteTagsReducer } from "./store/MultisiteTagsStore";
import { sitesMetadataReducer } from "./store/SitesStore";
import { multiSitesTagsChartReducer } from "./store/TagsChartStore";
import { multiSitesTrackersCommandsReducer } from "./store/TrackersCommandsStore";
import { multiSitesTrackersReducer } from "./store/TrackersStore";
import { multiSitesUserRecordsReducer } from "./store/UserRecordsStore";
import { multiSitesMaintenanceJobsReducer } from "./store/MaintenanceJobsStore";
import { userManagementReducer } from "./store/UserManagementStore";
import { pagesReducer } from "../../pages/PagesStore";
import { multiSitesFaultsReducer } from "./store/FaultsStore";
import { trendsReducer } from "../multisite_trends/TrendsStore";
import { siteReducer, defaultProperties as defaultSiteProperties } from "../sites/SiteStore";
import { userReducer } from "../user/UserStore";
import { safeOverrideReducer } from "./store/SafeOverrideStore";
import { featureFlagsReducer } from "../featureFlags/SitesFeatureFlagsStore";
import { scheduleCommandReducer } from "../trackers/ScheduleCommand/scheduleCommandStore";
import { diffuseOptimizationReducer } from "../diffuseOptimization/DiffuseOptimizationStore";
import { alertsSubscribersReducer } from "./store/AlertsSubscribersStore";
import { siteConfigurationPageReducer } from "./store/SiteConfigurationPageStore";
import { deploymentsPageReducer } from "./store/DeploymentsPageStore";
import { camerasReducer } from "../cameras/camerasStore";
import { subscriptionsReducer } from "../subscriptions/SubscriptionsStore";
import { purchasedProductsReducer } from "../purchasedProducts/PurchasedProductsStore";
import { chatReducer } from "../chat/chatStore";
import { IUserData } from "../user/Models";
import { lossAnalysisReducer } from "./store/LossAnalysisStore";
import { trackerComponentReplacementDialogReducer } from "../trackers/ReplacementCommand/ReplacementDialogStore";
import { siteSelectionChanged, applicationSessionChanged } from "./store/GlobalActions";
import { applicationSessionReducer } from "./store/applicationSessionStore";
import { createApplicationSessionMiddleware } from "./store/applicationSessionMiddleware";
import { faultHistoryReducer } from "../faults/history/FaultHistoryStore";
import { siteDesignMetadataReducer } from "../trackers/SiteDesignMetadataStore";
import { multiSiteConfigurationReducer } from "../multi_sites/MultiSiteConfigurationStore";
import { siteConfigurationDataReducer } from "../sites_configuration/SiteConfigurationDataStore";
import { agriPlanReducer } from "./agri/AgriPlanStore";
import { reportsReducer } from "../reports/ReportsStore";

const userPersistConfig = {
  key: "user",
  storage: localStorage,
  // Identity, permissions and sudo state must come from the current authenticated session.
  whitelist: [],
  stateReconciler: false as const,
};

const faultsReducer = combineReducers({
  multiSitesFaults: multiSitesFaultsReducer,
  subscribers: alertsSubscribersReducer,
});

const userPersistedReducer: Reducer<IUserData, AnyAction> = persistReducer<IUserData, AnyAction>(
  userPersistConfig,
  userReducer
);

const appReducer = combineReducers({
  session: applicationSessionReducer,
  user: userPersistedReducer,
  pages: pagesReducer,
  trends: trendsReducer,
  multiSitesUserRecords: multiSitesUserRecordsReducer,
  multiSitesMaintenanceJobs: multiSitesMaintenanceJobsReducer,
  userManagement: userManagementReducer,
  multiSitesTrackersCommands: multiSitesTrackersCommandsReducer,
  site: siteReducer,
  multiSitesMetadata: sitesMetadataReducer,
  multiSitesTags: multisiteTagsReducer,
  multiSitesChartsData: multiSitesTagsChartReducer,
  multiSitesTrackers: multiSitesTrackersReducer,
  safeOverride: safeOverrideReducer,
  featureFlags: featureFlagsReducer,
  scheduleCommand: scheduleCommandReducer,
  diffuseOptimization: diffuseOptimizationReducer,
  cameras: camerasReducer,
  faults: faultsReducer,
  faultHistory: faultHistoryReducer,
  siteDesignMetadata: siteDesignMetadataReducer,
  multiSiteConfiguration: multiSiteConfigurationReducer,
  siteConfigurationData: siteConfigurationDataReducer,
  agriPlans: agriPlanReducer,
  reports: reportsReducer,
  siteConfigurationPage: siteConfigurationPageReducer,
  deploymentsPage: deploymentsPageReducer,
  purchasedProducts: purchasedProductsReducer,
  subscriptions: subscriptionsReducer,
  lossAnalysis: lossAnalysisReducer,
  chat: chatReducer,
  trackerComponentReplacementDialog: trackerComponentReplacementDialogReducer,
});

export type RootState = ReturnType<typeof appReducer>;

export function rootReducer(state: RootState | undefined, action: AnyAction): RootState {
  if (
    state &&
    applicationSessionChanged.match(action) &&
    action.payload.identityKey !== state.session.identityKey
  ) {
    return {
      ...appReducer(undefined, { type: "application/initialState" }),
      session: applicationSessionReducer(state.session, action),
    };
  }
  if (!state || !siteSelectionChanged.match(action)) {
    return appReducer(state, action);
  }
  const { siteId } = action.payload;
  const selectedSite = siteId ? state.multiSitesMetadata.sites[siteId] : defaultSiteProperties;
  if (!selectedSite) {
    return state;
  }
  if (state.site.siteId === siteId) {
    return state.site === selectedSite ? state : { ...state, site: selectedSite };
  }
  const initial = appReducer(undefined, { type: "application/initialState" });
  return {
    ...state,
    site: selectedSite,
    pages: initial.pages,
    trends: initial.trends,
    safeOverride: initial.safeOverride,
    diffuseOptimization: initial.diffuseOptimization,
    scheduleCommand: initial.scheduleCommand,
    cameras: initial.cameras,
    multiSitesTrackersCommands: initial.multiSitesTrackersCommands,
    trackerComponentReplacementDialog: initial.trackerComponentReplacementDialog,
    multiSitesTrackers: { ...state.multiSitesTrackers, selectedTrackersIds: [] },
  };
}

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Only redux-persist's own protocol carries callbacks. Domain arguments are checked too.
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        ignoredActionPaths: [],
      },
    }).prepend(createApplicationSessionMiddleware()),
});

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
